import React, { useEffect, useState } from 'react';
import { Layout, Menu } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  ShoppingOutlined,
  TagsOutlined,
  ShopOutlined,
  TeamOutlined,
  FileTextOutlined,
  BarChartOutlined,
  SettingOutlined,
  ScanOutlined,
  InboxOutlined,
  TruckOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import { RobotOutlined } from '@ant-design/icons';
import useStore from '../../store/useStore';

const { Sider } = Layout;

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sidebarCollapsed, setSidebarCollapsed, themeMode } = useStore();
  const isDark = themeMode === 'dark';

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: 'inventory',
      icon: <InboxOutlined />,
      label: 'Inventory',
      children: [
        {
          key: '/products',
          icon: <ShoppingOutlined />,
          label: 'Products',
        },
        {
          key: '/categories',
          icon: <TagsOutlined />,
          label: 'Categories',
        },
        {
          key: '/locations',
          icon: <ShopOutlined />,
          label: 'Locations',
        },
        {
          key: '/barcode',
          icon: <ScanOutlined />,
          label: 'Barcode Scanner',
        },
        {
          key: '/stock',
          icon: <InboxOutlined />,
          label: 'Stock',
        },
        {
          key: '/e-waybills',
          icon: <FileTextOutlined />,
          label: 'E-Waybills',
        }, 
      ],
    },
    {
      key: 'sales',
      icon: <FileTextOutlined />,
      label: 'Sales',
      children: [
        {
          key: '/orders',
          icon: <FileTextOutlined />,
          label: 'Orders',
        },
        {
          key: '/customers',
          icon: <TeamOutlined />,
          label: 'Customers',
        },
        {
          key: '/quotes',
          icon: <FileTextOutlined />,
          label: 'Quotes',
        },
      ],
    },
    {
      key: 'purchasing',
      icon: <TruckOutlined />,
      label: 'Purchasing',
      children: [
        {
          key: '/purchase-orders',
          icon: <FileTextOutlined />,
          label: 'Purchase Orders',
        },
        {
          key: '/vendors',
          icon: <TeamOutlined />,
          label: 'Vendors',
        },
        {
          key: '/receiving',
          icon: <InboxOutlined />,
          label: 'Receiving',
        },
      ],
    },
    {
      key: '/reports',
      icon: <BarChartOutlined />,
      label: 'Reports',
    },
    {
      key: '/automation',
      icon: <RobotOutlined />,
      label: 'Automation',
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: 'Settings',
    },
  ];

  const handleMenuClick = ({ key }) => {
    if (key.startsWith('/')) {
      navigate(key);
    }
  };

  const getSelectedKeys = () => {
    const path = location.pathname;
    return [path];
  };

  // Accordion behavior: only one root submenu open
  const [openKeys, setOpenKeys] = useState([]);

  const computeRootKeyFromPath = (path) => {
    if (path.includes('/products') || path.includes('/categories') || path.includes('/locations') || path.includes('/barcode') || path.includes('/stock') || path.includes('/e-waybills')) {
      return 'inventory';
    }
    if (path.includes('/orders') || path.includes('/customers') || path.includes('/quotes') || path.includes('/eway-bills')) {
      return 'sales';
    }
    if (path.includes('/purchase-orders') || path.includes('/vendors') || path.includes('/receiving')) {
      return 'purchasing';
    }
    return undefined;
  };

  useEffect(() => {
    const root = computeRootKeyFromPath(location.pathname);
    setOpenKeys(root ? [root] : []);
  }, [location.pathname]);

  const onOpenChange = (keys) => {
    const rootSubmenus = ['inventory', 'sales', 'purchasing'];
    const latest = keys.find((k) => !openKeys.includes(k));
    if (rootSubmenus.includes(latest)) {
      setOpenKeys([latest]);
    } else {
      // closed last root submenu
      setOpenKeys(keys.filter((k) => rootSubmenus.includes(k)).slice(-1));
    }
  };

  return (
    <Sider
      collapsible
      collapsed={sidebarCollapsed}
      onCollapse={setSidebarCollapsed}
      trigger={null}
      width={256}
      className="bg-white dark:bg-slate-900 backdrop-blur-sm border-r border-gray-200 dark:border-slate-700 shadow-md ring-1 ring-primary-100 dark:ring-slate-800 bg-gradient-to-b from-primary-100 to-transparent dark:from-slate-800"
      breakpoint="lg"
      collapsedWidth={80}
    >
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
            <InboxOutlined className="text-white text-lg" />
          </div>
          {!sidebarCollapsed && (
            <h1 className="text-lg font-semibold text-gray-800 dark:text-slate-100 m-0">
              QQINV
            </h1>
          )}
        </div>
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
          aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? (
            <MenuUnfoldOutlined className="text-gray-600 dark:text-gray-300" />
          ) : (
            <MenuFoldOutlined className="text-gray-600 dark:text-gray-300" />
          )}
        </button>
      </div>

      <Menu
        mode="inline"
        selectedKeys={getSelectedKeys()}
        openKeys={openKeys}
        onOpenChange={onOpenChange}
        items={menuItems}
        onClick={handleMenuClick}
        theme={isDark ? 'dark' : 'light'}
        className="border-none h-full bg-transparent"
        style={{ borderRight: 0 }}
      />
    </Sider>
  );
};

export default Sidebar;
